import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Card } from 'components/shadcn/ui/card';
import { Button } from 'components/shadcn/ui/button';
import HGFormInput from 'components/HGForm/HGFormInput';
import HGFormSubmitBtn from 'components/HGForm/HGFormSubmitBtn';
import { addSpaceBeforeEveryCapitalLetter } from 'components/HGForm/helpers';
import HGError from 'components/HGForm/HGError';
import getValidationSchema from 'components/HGForm/formValidations';
import HGSuccessCard from 'components/HGSuccessCard';
import { useVerifyAdmin, useJwtToken } from 'helper';
import { useGetIDsFromURL } from 'components/DeviceDetails/helper';
import { useDevice } from 'components/DeviceDetails/service';
import { DeviceTypes } from '../constants';
import { updateDeviceProfile, useDeviceProfileFields } from './service';
import { Skeleton } from 'components/shadcn/ui/skeleton';
import DetachSiteButton from './DetachSiteButton';
import DetachSiteConfirmation from './DetachSiteConfirmation';

const EditDeviceProfile = () => {
    const { deviceProfileFields, loading, refetchProfileData } =
        useDeviceProfileFields();
    const { Thing_ID, pathname } = useGetIDsFromURL();
    const { data: device, refetch: refetchDevice } = useDevice(Thing_ID);
    const accessToken = useJwtToken();
    const isAdmin = useVerifyAdmin();
    const [errorMessage, setErrorMessage] = useState(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    const title =
        deviceProfileFields?.type === DeviceTypes.MODULE
            ? 'Module'
            : deviceProfileFields?.type === DeviceTypes.BMS
              ? 'Stack'
              : deviceProfileFields?.type
                ? 'Gateway'
                : '';

    const fieldKeysArray = Object.keys(deviceProfileFields ?? {});
    const validationSchema = useMemo(() => {
        const schemaShape = fieldKeysArray?.reduce((acc, key) => {
            if (key === 'WarrantyStart') {
                acc[key] = Yup.string();
            } else {
                const schema = getValidationSchema(key);
                const fieldLabel = addSpaceBeforeEveryCapitalLetter(key);
                acc[key] = schema.required(`${fieldLabel} is required`);
            }

            return acc;
        }, {});
        return Yup.object().shape(schemaShape);
    }, [fieldKeysArray]);

    const save = async (updatedDevice) => {
        try {
            const updatedProfile = await updateDeviceProfile(
                updatedDevice,
                device,
                accessToken
            );
            if (updatedProfile) {
                console.log('Updated profile:', updatedProfile);
                await refetchDevice();
                await refetchProfileData();
                setSuccess(true);
            }
        } catch (error) {
            console.error('Error during save operation:', error);
            setErrorMessage(
                error?.message ||
                    'An unexpected error occurred during the update.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = (values) => {
        setErrorMessage(null);
        setSubmitting(true);
        if (values?.siteAttachedTo === 'marked for detachment') {
            setOpenDeleteModal(true);
        } else {
            save(values);
        }
    };

    const routeBackToDevice = pathname.split('/').slice(0, -1).join('/');

    if (success)
        return (
            <HGSuccessCard
                buttons={[
                    {
                        buttonText: `go to ${title}`,
                        onClick: () => navigate(`${routeBackToDevice}`),
                    },
                ]}
            >
                {title} updated successfully.
                <br />
                Click below to go to {title} profile.
            </HGSuccessCard>
        );

    return (
        <Card className="p-5">
            {loading ? (
                <div className="space-y-5">
                    <Skeleton className="h-6 w-48" />
                    <div className="my-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                        {Array.from({ length: 6 }).map((_, idx) => (
                            <Skeleton key={idx} className="h-10 w-full" />
                        ))}
                    </div>
                    <Skeleton className="h-10 w-32" />
                </div>
            ) : (
                <>
                    <h4>Edit {title} Profile</h4>
                    <Formik
                        initialValues={deviceProfileFields}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        validateOnBlur={false}
                        enableReinitialize
                    >
                        {({ values, setFieldValue }) => (
                            <Form>
                                <div className="my-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                                    {fieldKeysArray?.map((key) => {
                                        return (
                                            <div className="relative">
                                                <HGFormInput
                                                    key={key}
                                                    name={key}
                                                    label={
                                                        key === 'WarrantyStart'
                                                            ? 'Warranty'
                                                            : addSpaceBeforeEveryCapitalLetter(
                                                                  key
                                                              )
                                                    }
                                                    variant="standard"
                                                    type={
                                                        key === 'WarrantyStart'
                                                            ? 'date'
                                                            : 'text'
                                                    }
                                                    disabled={
                                                        key === 'name' ||
                                                        (isAdmin &&
                                                            key ===
                                                                'WarrantyStart')
                                                            ? false
                                                            : true
                                                    }
                                                    className="w-full"
                                                    InputProps={
                                                        key ===
                                                            'warrantyStart' && {
                                                            inputProps: {
                                                                max: '9999-12-31',
                                                            },
                                                        }
                                                    }
                                                />
                                                {isAdmin &&
                                                    key === 'siteAttachedTo' &&
                                                    device?.Site_ID && (
                                                        <DetachSiteButton
                                                            initialValues={
                                                                deviceProfileFields
                                                            }
                                                            setFieldValue={
                                                                setFieldValue
                                                            }
                                                        />
                                                    )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {errorMessage && (
                                    <HGError errorMessage={errorMessage} />
                                )}
                                <div className="mt-5 flex justify-end">
                                    <Button
                                        variant="destructive"
                                        onClick={() => navigate(-1)}
                                        className="mr-2"
                                    >
                                        cancel
                                    </Button>
                                    <HGFormSubmitBtn
                                        loading={submitting}
                                        disabled={
                                            values?.name ===
                                                deviceProfileFields?.name &&
                                            values?.siteAttachedTo ===
                                                deviceProfileFields?.siteAttachedTo &&
                                            (!isAdmin ||
                                                (isAdmin &&
                                                    values?.warranty ===
                                                        deviceProfileFields?.warranty))
                                                ? true
                                                : false
                                        }
                                    >
                                        save
                                    </HGFormSubmitBtn>
                                </div>
                                <DetachSiteConfirmation
                                    open={openDeleteModal}
                                    close={() => {
                                        console.log('cancel');
                                        setOpenDeleteModal(false);
                                        setSubmitting(false);
                                    }}
                                    confirm={() => {
                                        console.log('confirm');
                                        setOpenDeleteModal(false);
                                        save(values);
                                    }}
                                />
                            </Form>
                        )}
                    </Formik>
                </>
            )}
        </Card>
    );
};

export default EditDeviceProfile;
